import { FunctionComponent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Typography } from '@mui/material';
import {
    Avatar,
    Label,
    PriorityDropdown,
    PriorityInfoModal,
    ConfirmationDialog
} from 'src/shared';
import { ReactComponent as InfoIcon } from 'src/assets/icons/information.svg';
import {
    DetailsWrapper,
    FlexWrapper,
    AvatarWithText,
    projectIdStyle
} from '../../styled';
import { useSelector } from 'react-redux';
import { getProject } from 'src/redux/projects/selectors';
import theme from 'src/theme';
import {
    S,
    PriorityWrapper,
    PriorityLabelWithIcon,
    InfoIconButton,
    LlmInfoWrapper,
    LlmInfoLabel,
    ViewSessionsButton
} from './styled';
import { ClientSelectors } from 'src/redux/client/selectors';
import { isAdmin } from 'src/redux/auth/service';
import { updateProject } from 'src/redux/onboarding/service';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { RoutesEnum } from 'src/types/navigation';

const ProjectDetails: FunctionComponent = () => {
    const navigate = useNavigate();
    const { selectedProjectId } = ClientSelectors();
    const selectedProject = useSelector(getProject(selectedProjectId));
    const dispatch = useAppDispatch();
    const admin = isAdmin();
    const [urgentPriorityDialogOpen, setUrgentPriorityDialogOpen] =
        useState(false);
    const [priorityInfoModalOpen, setPriorityInfoModalOpen] = useState(false);

    const handlePriorityChange = useCallback(
        async (priority: number, skipConfirmation: boolean = false) => {
            try {
                if (!selectedProjectId) {
                    console.error('Project ID is missing');
                    return;
                }

                // Special case: admin selecting priority 10 (urgent) - show confirmation dialog
                if (admin && priority === 10 && !skipConfirmation) {
                    setUrgentPriorityDialogOpen(true);
                    return;
                }

                // Close dialog if it was open
                if (skipConfirmation) {
                    setUrgentPriorityDialogOpen(false);
                }

                await dispatch(
                    updateProject({
                        projectId: Number(selectedProjectId),
                        priority: priority
                    })
                ).unwrap();

                // No need to call getAllProjects() - the projects slice listens to
                // updateProject.fulfilled and updates the store automatically
            } catch (error) {
                console.error('Failed to update project priority:', error);
            }
        },
        [selectedProjectId, dispatch, admin]
    );

    const handleCancelUrgentPriority = useCallback(() => {
        setUrgentPriorityDialogOpen(false);
    }, []);

    return (
        <DetailsWrapper>
            <AvatarWithText>
                <Avatar
                    background={theme.color.darkBlue}
                    size="110px"
                    image=""
                />
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            pl: 1,
                            fontWeight: 'bold',
                            color: theme.color.white90
                        }}
                    >
                        {selectedProject?.projectName || 'Project Name'}
                    </Typography>
                    {selectedProjectId && (
                        <Label
                            sxOverrides={projectIdStyle}
                        >{`project id: ${selectedProjectId}`}</Label>
                    )}
                    {selectedProject && (
                        <PriorityWrapper>
                            <PriorityLabelWithIcon>
                                Priority
                                {admin && (
                                    <InfoIconButton
                                        onClick={() =>
                                            setPriorityInfoModalOpen(true)
                                        }
                                        type="button"
                                    >
                                        <InfoIcon />
                                    </InfoIconButton>
                                )}
                            </PriorityLabelWithIcon>
                            <PriorityDropdown
                                priority={selectedProject.priority}
                                onPriorityChange={handlePriorityChange}
                                isAdmin={admin}
                                disabled={false}
                                showLabel={false}
                            />
                        </PriorityWrapper>
                    )}
                    {admin && selectedProjectId && (
                        <LlmInfoWrapper>
                            <LlmInfoLabel>LLM Info</LlmInfoLabel>
                            <ViewSessionsButton
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                    navigate(
                                        RoutesEnum.PROJECT_LLM_INFO.replace(
                                            ':id',
                                            selectedProjectId
                                        )
                                    )
                                }
                            >
                                View Sessions
                            </ViewSessionsButton>
                        </LlmInfoWrapper>
                    )}
                </Box>
            </AvatarWithText>

            <ConfirmationDialog
                open={urgentPriorityDialogOpen}
                onClose={handleCancelUrgentPriority}
                onConfirm={() => handlePriorityChange(10, true)}
                title="Urgent Request"
                message="You are about to set this request as urgent. This will prioritize it above all other requests. Are you sure you want to proceed?"
            />

            <PriorityInfoModal
                open={priorityInfoModalOpen}
                onClose={() => setPriorityInfoModalOpen(false)}
            />

            <FlexWrapper
                sx={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: '5px'
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'none', sm: 'none', md: 'block' }
                        }}
                    >
                        <Label
                            sxOverrides={{
                                ...S.statusText,
                                marginRight: '8px'
                            }}
                        >
                            Status
                        </Label>
                    </Box>
                    <Chip label="Active" sx={S.statusChip} />
                </Box>

                <Box
                    sx={{
                        display: { xs: 'none', sm: 'block', md: 'block' }
                    }}
                >
                    <Label sxOverrides={S.sinceText}>
                        {selectedProject?.date
                            ? `since ${new Date(
                                  selectedProject.date
                              ).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                              })}`
                            : ''}
                    </Label>
                </Box>
            </FlexWrapper>
        </DetailsWrapper>
    );
};

export default ProjectDetails;
